import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, LayoutList, Trash2, Edit2, Globe, Lock, Users } from "lucide-react";
import { toast } from "sonner";

export default function Lists() {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/lists`);
      if (response.ok) {
        const data = await response.json();
        setLists(data);
      }
    } catch (error) {
      console.error("Error fetching lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setIsPublic(true);
    setEditingList(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (list) => {
    setEditingList(list);
    setName(list.name);
    setDescription(list.description || "");
    setIsPublic(list.is_public);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter a list name");
      return;
    }

    setSubmitting(true);
    try {
      const url = editingList
        ? `${API}/lists/${editingList.list_id}`
        : `${API}/lists`;
      
      const response = await authFetch(url, {
        method: editingList ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          is_public: isPublic,
          album_ids: editingList?.album_ids || [],
        }),
      });

      if (response.ok) {
        toast.success(editingList ? "List updated!" : "List created!");
        setDialogOpen(false);
        resetForm();
        fetchLists();
      } else {
        toast.error("Failed to save list");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!listToDelete) return;
    
    setDeleting(true);
    try {
      const response = await authFetch(`${API}/lists/${listToDelete}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("List deleted");
        setDeleteDialogOpen(false);
        setListToDelete(null);
        fetchLists();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete list");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong");
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (listId) => {
    setListToDelete(listId);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="lists-page">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-purple-900/10 pointer-events-none" />
      
      <div className="relative max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-white">
              Your Lists
            </h1>
            <p className="text-white/60">
              Curate and organize your favorite albums.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreateDialog}
                className="rounded-full"
                data-testid="create-list-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New List
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl text-white">
                  {editingList ? "Edit List" : "Create New List"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium mb-2 block text-white">
                    List Name
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Best Albums of 2024"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-purple-500"
                    data-testid="list-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-sm font-medium mb-2 block text-white">
                    Description (optional)
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="What's this list about?"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-purple-500 min-h-[80px]"
                    data-testid="list-description-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isPublic ? (
                      <Globe className="w-4 h-4 text-white/50" />
                    ) : (
                      <Lock className="w-4 h-4 text-white/50" />
                    )}
                    <Label htmlFor="public" className="text-sm text-white">
                      {isPublic ? "Public" : "Private"}
                    </Label>
                  </div>
                  <Switch
                    id="public"
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                    data-testid="list-public-switch"
                  />
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full rounded-full py-6 font-bold"
                  data-testid="save-list-btn"
                >
                  {submitting ? "Saving..." : editingList ? "Update List" : "Create List"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Lists grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="glass-card rounded-xl p-4 animate-pulse">
                <div className="flex gap-2 mb-3">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="w-12 h-12 rounded bg-muted" />
                  ))}
                </div>
                <div className="w-3/4 h-5 bg-muted rounded mb-2" />
                <div className="w-1/2 h-4 bg-muted rounded" />
              </div>
            ))}
          </div>
        ) : lists.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {lists.map((list) => (
              <div
                key={list.list_id}
                className="glass-card rounded-xl p-4 hover:border-purple-500/30 transition-colors group"
                data-testid={`list-card-${list.list_id}`}
              >
                <Link to={`/list/${list.list_id}`}>
                  <div className="flex gap-2 mb-3">
                    {list.albums?.slice(0, 4).map((album, i) => (
                      <img
                        key={album.album_id}
                        src={album.cover_url}
                        alt={album.title}
                        className="w-12 h-12 rounded object-cover"
                        style={{ marginLeft: i > 0 ? "-8px" : 0, zIndex: 4 - i }}
                      />
                    ))}
                    {(!list.albums || list.albums.length === 0) && (
                      <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center">
                        <LayoutList className="w-6 h-6 text-white/30" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold truncate text-white group-hover:text-purple-400 transition-colors">
                    {list.name}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-white/50">
                    <span>{list.album_ids?.length || 0} albums</span>
                    {!list.is_public && <Lock className="w-3 h-3" />}
                    {(list.collaborators?.length > 0 || list.is_collaborator) && (
                      <span className="inline-flex items-center gap-1 text-xs text-purple-400">
                        <Users className="w-3 h-3" />
                        {list.is_collaborator ? "Shared with you" : `${list.collaborators.length} collaborator${list.collaborators.length > 1 ? "s" : ""}`}
                      </span>
                    )}
                  </div>
                </Link>

                {/* Actions - only for owned lists */}
                {!list.is_collaborator && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openEditDialog(list);
                    }}
                    className="flex-1 text-white/70 hover:text-white hover:bg-white/5"
                    data-testid={`edit-list-${list.list_id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openDeleteDialog(list.list_id);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-list-${list.list_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card rounded-xl p-12 text-center">
            <LayoutList className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-white">No lists yet</h3>
            <p className="text-white/60 mb-6">
              Create your first list to start curating albums.
            </p>
            <Button onClick={openCreateDialog} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First List
            </Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Delete List</DialogTitle>
            <DialogDescription className="text-white/60">
              Are you sure you want to delete this list? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
